import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { CurrentLangService } from '@core/services/common/current-lang.service';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { GlobalShortcutsService } from '@core/services/shortcuts.service';
import { PlanEditSessionActions as Session } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectPlanEditSession,
  unsavedEditCount
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { selectPlanWatch } from '@core/store/plan-edit/plan-edit.selectors';
import { warnOnTabCloseWhile } from '@core/utils/before-unload-warning';

import {
  ModalComponent,
  ModalConfig,
  ModalSize,
  ModalType
} from '@shared/components/modal/modal.component';
import { Shortcut } from '@shared/utils/shortcut';

import { PlanFindingsPanelComponent } from '../../findings-panel/plan-findings-panel.component';
import { PlanEditDialogsService } from './plan-edit-dialogs.service';
import { PlanEditLeavePromptService } from './plan-edit-leave-prompt.service';

export const PLAN_EDIT_UNDO_SHORTCUT = 'Meta.z';
export const PLAN_EDIT_REDO_SHORTCUT = 'Meta.Shift.z';

const KEY = 'features.plan.edit.dialogs';

/**
 * The plan edit dialogs, shortcuts and tab-close warning, rendered once by the root layout so they
 * hold on every page of the plan, including those that show no edit bar.
 */
@Component({
  selector: 'app-plan-edit-dialogs',
  imports: [ModalComponent, PlanFindingsPanelComponent, TranslatePipe],
  templateUrl: './plan-edit-dialogs.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``
})
export class PlanEditDialogsComponent {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly lang = inject(CurrentLangService).currentLang;
  private readonly session = this.store.selectSignal(selectPlanEditSession);
  private readonly watch = this.store.selectSignal(selectPlanWatch);

  protected readonly dialogs = inject(PlanEditDialogsService);
  protected readonly leavePrompt = inject(PlanEditLeavePromptService);
  protected readonly entityTypeLabels = inject(EntityDisplayNamesService)
    .singularDisplayNames;
  protected readonly ModalSize = ModalSize;

  protected readonly mode = computed(() => this.session().mode);
  protected readonly unsavedCount = computed(() => unsavedEditCount(this.session()));
  protected readonly findings = computed(() => {
    const watch = this.watch();
    const session = this.session();
    return session.planId !== null && watch.planId === session.planId
      ? (watch.findings ?? [])
      : [];
  });

  protected readonly findingsConfig = computed<ModalConfig>(() => {
    this.lang();
    return {
      icon: 'warning',
      type: ModalType.WARNING,
      dismissible: true,
      closeOnBackdropClick: true,
      actions: [
        {
          label: this.translate.instant(`${KEY}.findings.cancel`),
          callback: () => this.dialogs.findings.set(null),
          color: 'cancel'
        },
        {
          label: this.translate.instant(`${KEY}.findings.save_anyway`),
          callback: () => this.saveAnyway(),
          disabled: this.mode() !== 'editing',
          color: 'primary'
        }
      ]
    };
  });

  protected readonly discardConfig = computed<ModalConfig>(() => {
    this.lang();
    return {
      icon: 'trash',
      type: ModalType.ERROR,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      actions: [
        {
          label: this.translate.instant(`${KEY}.discard.keep`),
          callback: () => this.dialogs.discard.set(false),
          color: 'cancel'
        },
        {
          label: this.translate.instant(`${KEY}.discard.confirm`),
          callback: () => this.confirmDiscard(),
          color: 'danger'
        }
      ]
    };
  });

  protected readonly leaveConfig = computed<ModalConfig>(() => {
    this.lang();
    return {
      icon: 'warning',
      type: ModalType.WARNING,
      dismissible: true,
      closeOnBackdropClick: true,
      askingModal: true,
      actions: [
        {
          label: this.translate.instant(`${KEY}.leave.stay`),
          callback: () => this.leavePrompt.answer('stay'),
          color: 'cancel'
        },
        {
          label: this.translate.instant(`${KEY}.leave.discard`),
          callback: () => this.leavePrompt.answer('discard'),
          color: 'danger'
        },
        {
          label: this.translate.instant(`${KEY}.leave.save`),
          callback: () => this.leavePrompt.answer('save'),
          // A save already in flight is waited for; any other mode cannot save.
          disabled: this.mode() !== 'editing' && this.mode() !== 'saving',
          color: 'primary'
        }
      ]
    };
  });

  constructor() {
    const shortcuts = inject(GlobalShortcutsService);
    const undo = new Shortcut(PLAN_EDIT_UNDO_SHORTCUT, () =>
      this.store.dispatch(Session.undoRequested())
    );
    const redo = new Shortcut(PLAN_EDIT_REDO_SHORTCUT, () =>
      this.store.dispatch(Session.redoRequested())
    );
    const editing = computed(() => this.mode() !== 'off');
    effect(() => {
      if (editing()) {
        shortcuts.register(undo);
        shortcuts.register(redo);
      } else {
        shortcuts.unregister(undo);
        shortcuts.unregister(redo);
        // A dialog the session outlived would act on nothing.
        this.dialogs.findings.set(null);
        this.dialogs.discard.set(false);
      }
    });
    inject(DestroyRef).onDestroy(() => {
      shortcuts.unregister(undo);
      shortcuts.unregister(redo);
    });
    warnOnTabCloseWhile(computed(() => editing() && this.unsavedCount() > 0));
  }

  protected countKey(base: string, count: number): string {
    return `${KEY}.${base}.${count === 1 ? 'one' : 'other'}`;
  }

  private saveAnyway(): void {
    this.dialogs.findings.set(null);
    this.store.dispatch(Session.saveRequested());
  }

  private confirmDiscard(): void {
    this.dialogs.discard.set(false);
    this.store.dispatch(Session.discardRequested());
  }
}
